import {
  interpolate,
  spring,
  Sequence,
  useCurrentFrame,
  useVideoConfig,
} from 'remotion';

// Icons
import { CircleRing } from '@shared/icons/CircleRing';
import { CIRCLE_RING_ANIMATION } from '../constants';

// Constants
import { ANIMATION_SPRING_CONFIGS, CLAMP_RIGHT } from '@/constants';

// Themes
import { employeeYearInReviewTheme } from '@/components/EmployeeYearInReview/theme';

interface DecorativeCircleProps {
  from?: number;
  durationInFrames?: number;
}

export const DecorativeCircle = ({
  from = 0,
  durationInFrames,
}: DecorativeCircleProps) => {
  const { colors } = employeeYearInReviewTheme;
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  // Reveal animation from left to right (like a rainbow appearing)
  const progress = spring({
    frame: frame * CIRCLE_RING_ANIMATION.REVEAL_SPEED,
    fps,
    config: ANIMATION_SPRING_CONFIGS[CIRCLE_RING_ANIMATION.SPRING_CONFIG],
  });

  // Calculate the reveal percentage (0% = fully hidden, 100% = fully visible)
  const revealPercentage = progress * 100;

  // Slide animation from left to right (slower speed)
  const slideProgress = spring({
    frame: frame * CIRCLE_RING_ANIMATION.SLIDE_SPEED,
    fps,
    config: ANIMATION_SPRING_CONFIGS[CIRCLE_RING_ANIMATION.SPRING_CONFIG],
  });

  // Translate from left (-100%) to final position (0%)
  const translateX = interpolate(slideProgress, [0, 1], [-100, 0], CLAMP_RIGHT);

  // Opacity fade in
  const opacity = interpolate(frame, [0, 10], [0, 1], CLAMP_RIGHT);

  return (
    <Sequence
      from={from}
      durationInFrames={durationInFrames}
      name="Decorative Circle"
    >
      {/* Background Decorative Circle - Right Side */}
      <div
        className="absolute -right-6 bottom-0"
        style={{
          transform: `translateY(68%) translateX(${translateX}%)`,
          opacity: opacity,
          clipPath: `inset(0 ${100 - revealPercentage}% 0 0)`,
        }}
      >
        <CircleRing className={colors.ringStroke} />
      </div>
    </Sequence>
  );
};
